// Engine wrapper around HTMLCanvasElement. Holds the canvas context, applies
// flood fills via the pure floodFill function, and manages undo history.
//
// API split:
//   - fillAt(x,y,color)              instant fill, mostly for tests / batch ops
//   - prepareFill(x,y,color)         async-friendly: compute fill without
//                                    committing to the canvas. The caller can
//                                    animate a ripple reveal, then candidate.commit().
//
// Pipeline invariant trust: we expect lineart from process-assets.mjs which
// is binarized (zero mid-gray pixels). With devChecks=true we sanity-check
// loaded images and console.warn if that invariant looks broken.

import { runMagicAutofill, type MagicFillResult } from './autofill.ts'
import { floodFill } from './floodfill.ts'
import { HistoryStack } from './history.ts'
import type {
  ColoringEngineOptions,
  FillResult,
  RGB,
  RequiredColoringEngineOptions,
} from './types.ts'

const DEFAULTS: RequiredColoringEngineOptions = {
  threshold: 50,
  maxUndoSteps: 8,
  minFillPixels: 100,
  maxFillRatio: 0.5,
  devChecks: false,
}

/** How many mid-gray pixels we tolerate before warning in devChecks. */
const MID_GRAY_WARN_LIMIT = 1000

export interface FillCandidate {
  result: FillResult & { status: 'filled' }
  /** Pre-fill canvas pixels — useful for the React layer to mask underneath the reveal. */
  before: ImageData
  /** Post-fill canvas pixels. Drawn into the mask during the reveal animation. */
  after: ImageData
  /** Click point (already floored to integer pixel). */
  startX: number
  startY: number
  color: RGB
  /** Apply the fill to the canvas + push history. Safe to call multiple times. */
  commit: () => void
  /** Discard the fill. Canvas stays at the pre-fill state. */
  cancel: () => void
}

export interface PrepareFillReturn {
  result: FillResult
  candidate: FillCandidate | null
}

/** Hard cap on home-region size for brush-stroke masking. If a flood fill
 *  from the brush-down point spreads past this many pixels, we treat the
 *  lineart as unclosed (likely leaking into the background) and skip the
 *  mask — falling back to per-stamp BFS, which is safer than locking the
 *  entire canvas to one stroke. ~45% of 1408×786 = ~500k. */
const MAX_STROKE_HOME_PIXELS = 500_000
/** Dilation rounds applied to the line mask before the home-region flood.
 *  Each round thickens lines by 1 px in every direction. Gaps narrower
 *  than 2N px get sealed (each side of the gap grows N pixels and they
 *  meet). After the home flood, we dilate the HOME REGION by the same
 *  N rounds — gated by the ORIGINAL line mask, not the dilated one —
 *  to reclaim the dilate-buffer pixels that are visually part of the
 *  region but were outside the BFS due to the thickened wall. This
 *  hybrid keeps gaps sealed (the closed gap pixels are still walls for
 *  the home flood AND for the home dilate) while restoring the painted
 *  region right up to the original lineart edge — no halo. */
const LINE_DILATE_ROUNDS = 3

export class ColoringEngine {
  readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  readonly width: number
  readonly height: number
  private readonly history: HistoryStack
  /** Home region for the active brush stroke (4-connected from the brush-
   *  down point, line pixels as walls). Null when no stroke is in flight,
   *  or when the start point landed on a line, or when the home region
   *  blew past MAX_STROKE_HOME_PIXELS. brushStamp uses this mask to keep
   *  the whole stroke inside one region — dragging across a line stops
   *  painting on the other side. */
  private strokeHomeRegion: Uint8Array | null = null
  readonly options: RequiredColoringEngineOptions

  constructor(canvas: HTMLCanvasElement, options: ColoringEngineOptions = {}) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('canvas 2d context unavailable')
    this.ctx = ctx
    this.width = canvas.width
    this.height = canvas.height
    this.options = { ...DEFAULTS, ...options }
    this.history = new HistoryStack(this.options.maxUndoSteps)
  }

  /** Draw a lineart and reset history (undo can return to this clean state). */
  loadImage(source: HTMLImageElement | ImageBitmap): void {
    this.ctx.clearRect(0, 0, this.width, this.height)
    this.ctx.drawImage(source, 0, 0, this.width, this.height)
    if (this.options.devChecks) this.checkBinarized()
    this.history.reset(this.snapshot())
  }

  /** Draw an arbitrary "lineart" provided as ImageData (tests + dev playground). */
  loadImageData(imageData: ImageData): void {
    if (imageData.width !== this.width || imageData.height !== this.height) {
      throw new Error('imageData dimensions must match canvas')
    }
    this.ctx.putImageData(imageData, 0, 0)
    if (this.options.devChecks) this.checkBinarized()
    this.history.reset(this.snapshot())
  }

  /**
   * Compute the fill result and produce a candidate the caller can either
   * commit instantly or animate first and commit on animation end.
   *
   * If the click is rejected (on-line / same-color / too-small / no-image),
   * candidate is null and the caller can read result.status for feedback.
   */
  prepareFill(x: number, y: number, color: RGB): PrepareFillReturn {
    const ix = Math.floor(x)
    const iy = Math.floor(y)
    const before = this.ctx.getImageData(0, 0, this.width, this.height)

    const afterData = new Uint8ClampedArray(before.data)
    const result = floodFill({
      data: afterData,
      width: this.width,
      height: this.height,
      startX: ix,
      startY: iy,
      replacement: color,
      threshold: this.options.threshold,
    })

    if (result.status !== 'filled') {
      return { result, candidate: null }
    }

    // Tiny fills (clicks landing in a sliver / anti-alias gap on a non-
    // binarized image) get demoted to 'too-small' and not committed. Avoids
    // burning an undo slot on a useless 8-pixel fill.
    const filledCount = result.filledPixelCount ?? 0
    if (filledCount <= this.options.minFillPixels) {
      return {
        result: { ...result, status: 'too-small' },
        candidate: null,
      }
    }

    // Catastrophic fills (lineart has an unclosed gap, bucket leaked across
    // regions) get rejected as 'too-large'. The pipeline's morphological
    // close pass takes care of small gaps, but a 5px+ break still slips
    // through — better to refuse the fill than let the kid paint half the
    // canvas one shade. Caller toasts a hint.
    const totalPixels = this.width * this.height
    if (filledCount > totalPixels * this.options.maxFillRatio) {
      return {
        result: { ...result, status: 'too-large' },
        candidate: null,
      }
    }

    const after = this.ctx.createImageData(this.width, this.height)
    after.data.set(afterData)

    let settled = false

    const candidate: FillCandidate = {
      result: result as FillResult & { status: 'filled' },
      before,
      after,
      startX: ix,
      startY: iy,
      color,
      commit: () => {
        if (settled) return
        settled = true
        this.ctx.putImageData(after, 0, 0)
        this.history.pushFill({
          data: after.data,
          width: this.width,
          height: this.height,
        })
      },
      cancel: () => {
        if (settled) return
        settled = true
      },
    }

    return { result, candidate }
  }

  /** Instant fill — equivalent to prepareFill().candidate.commit(). */
  fillAt(x: number, y: number, color: RGB): FillResult {
    const { result, candidate } = this.prepareFill(x, y, color)
    if (candidate) candidate.commit()
    return result
  }

  /**
   * Called by the brush tool on pointer-down. Floods 4-connected from
   * (x, y) with lineart pixels as walls, building a home-region mask that
   * subsequent brushStamp calls in this stroke MUST stay inside. Solves
   * 主公's "還是可以亂畫" feedback: per-stamp BFS only stops within one
   * stamp circle, but dragging across a line lets the next stamp's BFS
   * paint the other side from a new center. The stroke-wide home mask
   * locks the whole drag to one region.
   *
   * Skips the mask (and lets brushStamp fall back to its own BFS) when:
   *   (a) start point is out of bounds or on a line pixel — kid clicked
   *       a line; we don't lock anything. Subsequent stamps work as
   *       BFS-from-center.
   *   (b) the home region grows past MAX_STROKE_HOME_PIXELS — likely
   *       leaking through an unclosed lineart into the background; the
   *       safer behavior is per-stamp BFS, not "stroke locks the whole
   *       canvas".
   *
   * One-time cost per pointer-down on a typical region: ~10-50ms.
   */
  startBrushStroke(x: number, y: number): void {
    this.strokeHomeRegion = null
    const cx = Math.floor(x)
    const cy = Math.floor(y)
    if (cx < 0 || cx >= this.width || cy < 0 || cy >= this.height) return

    const imageData = this.ctx.getImageData(0, 0, this.width, this.height)
    const data = imageData.data
    const W = this.width
    const H = this.height
    const N = W * H

    // Pass 1: original line mask (R+G+B < 60). Used both as the source
    // for dilation AND as the gate when we expand the home region back
    // out — never crossed.
    const originalLine = new Uint8Array(N)
    for (let i = 0; i < N; i++) {
      const pi = i * 4
      if (data[pi]! + data[pi + 1]! + data[pi + 2]! < 60) originalLine[i] = 1
    }

    // Pass 2: dilate to seal small gaps. Each round grows lines by 1 px
    // in every direction; gaps narrower than 2 × LINE_DILATE_ROUNDS
    // become continuous wall.
    let dilatedLine = new Uint8Array(originalLine)
    for (let round = 0; round < LINE_DILATE_ROUNDS; round++) {
      const next = new Uint8Array(dilatedLine)
      for (let yy = 0; yy < H; yy++) {
        for (let xx = 0; xx < W; xx++) {
          const idx = yy * W + xx
          if (dilatedLine[idx]) continue
          if (
            (xx > 0 && dilatedLine[idx - 1]) ||
            (xx < W - 1 && dilatedLine[idx + 1]) ||
            (yy > 0 && dilatedLine[idx - W]) ||
            (yy < H - 1 && dilatedLine[idx + W])
          ) {
            next[idx] = 1
          }
        }
      }
      dilatedLine = next
    }

    // Find a flood-fill seed that's inside an actual region (not a line,
    // and not inside the dilate buffer). The kid's pointer-down can land
    // on a 1-2 px lineart pixel when the tap is quick — without this
    // search we'd bail and brushStamp would fall back to per-stamp BFS,
    // which doesn't enforce stroke-wide region lock, and a fast drag
    // would punch past the line that's right next to the start point.
    // (主公 dogfood: "下筆動作很快的時候, 線條就會衝出去, 但我若第一筆
    // 稍微慢一點, 就不會衝出去".)
    const SEED_SEARCH_RADIUS = 5
    let startIdx = cy * W + cx
    if (dilatedLine[startIdx]) {
      let found = false
      for (let r = 1; r <= SEED_SEARCH_RADIUS && !found; r++) {
        // Search the square ring at radius r, expanding outward.
        for (let dy = -r; dy <= r && !found; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            // Only consider the outermost ring of this radius
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue
            const nx = cx + dx
            const ny = cy + dy
            if (nx < 0 || nx >= W || ny < 0 || ny >= H) continue
            const nIdx = ny * W + nx
            if (!dilatedLine[nIdx]) {
              startIdx = nIdx
              found = true
              break
            }
          }
        }
      }
      if (!found) {
        // Truly surrounded by line work within the search radius — bail.
        // brushStamp will fall back to per-stamp BFS, which is also a
        // no-op for the on-line stamp anyway.
        return
      }
    }

    // Pass 3: flood the home region using the DILATED mask as walls.
    // This yields a "shrunk" home region — N pixels short of the
    // original lineart edge — because the dilated buffer pixels block
    // it from reaching the edge. We'll re-expand in pass 4.
    let homeMask = new Uint8Array(N)
    homeMask[startIdx] = 1
    const stack: number[] = [startIdx]
    let count = 1
    while (stack.length > 0) {
      const idx = stack.pop()!
      if (count > MAX_STROKE_HOME_PIXELS) {
        // Home region leaked even after dilation. Fall back to per-stamp BFS.
        this.strokeHomeRegion = null
        return
      }
      const px = idx % W
      const py = (idx - px) / W
      const tryPush = (nIdx: number): void => {
        if (homeMask[nIdx]) return
        if (dilatedLine[nIdx]) return
        homeMask[nIdx] = 1
        stack.push(nIdx)
        count++
      }
      if (px + 1 < W) tryPush(idx + 1)
      if (px > 0) tryPush(idx - 1)
      if (py + 1 < H) tryPush(idx + W)
      if (py > 0) tryPush(idx - W)
    }

    // Pass 4: dilate the HOME REGION outward by LINE_DILATE_ROUNDS, gated
    // by the ORIGINAL line (not the dilated one). Effect: the home region
    // grows back up to (but not across) the original lineart edge,
    // reclaiming the buffer pixels that pass 2 had reserved as wall.
    // Sealed gaps stay sealed: gap pixels sit on the OTHER side of the
    // gap relative to the home region, and home dilation can't reach them
    // because it would have to traverse the home region's own boundary
    // out into the unreached region — there's no path through the home
    // mask itself for it to do that. (Worst case for very small gaps is
    // covered by MAX_STROKE_HOME_PIXELS bail-out earlier.)
    for (let round = 0; round < LINE_DILATE_ROUNDS; round++) {
      const next = new Uint8Array(homeMask)
      for (let yy = 0; yy < H; yy++) {
        for (let xx = 0; xx < W; xx++) {
          const idx = yy * W + xx
          if (homeMask[idx]) continue
          if (originalLine[idx]) continue
          if (
            (xx > 0 && homeMask[idx - 1]) ||
            (xx < W - 1 && homeMask[idx + 1]) ||
            (yy > 0 && homeMask[idx - W]) ||
            (yy < H - 1 && homeMask[idx + W])
          ) {
            next[idx] = 1
          }
        }
      }
      homeMask = next
    }

    this.strokeHomeRegion = homeMask
  }

  /**
   * Brush stamp at (x, y) — paints a filled circle of `radius` canvas pixels
   * BFS-masked from the stamp center so lineart acts as a barrier even
   * within one stamp. If a stroke is in flight AND `startBrushStroke`
   * built a home-region mask, the stamp ALSO has to stay inside that mask
   * so dragging across a line stops painting on the other side.
   *
   * Stamp center on a line pixel = no-op (matches the bucket tool's
   * "on-line" reject; the stroke continues painting when the pointer
   * leaves the line).
   *
   * Does NOT push history. The caller batches one or more stamps into a
   * stroke and calls `commitBrushStroke()` on pointerup so a whole stroke
   * is one undo step rather than dozens.
   */
  brushStamp(x: number, y: number, radius: number, color: RGB): void {
    const r = Math.max(1, Math.floor(radius))
    const cx = Math.floor(x)
    const cy = Math.floor(y)
    const x0 = Math.max(0, cx - r)
    const x1 = Math.min(this.width - 1, cx + r)
    const y0 = Math.max(0, cy - r)
    const y1 = Math.min(this.height - 1, cy + r)
    if (x1 < x0 || y1 < y0) return

    const w = x1 - x0 + 1
    const h = y1 - y0 + 1
    const region = this.ctx.getImageData(x0, y0, w, h)
    const data = region.data
    const r2 = r * r
    const cr = color.r
    const cg = color.g
    const cb = color.b

    // Translate center into local (region-buffer) coordinates.
    const lcx = cx - x0
    const lcy = cy - y0
    if (lcx < 0 || lcx >= w || lcy < 0 || lcy >= h) return

    const centerI = (lcy * w + lcx) * 4
    // Center on a line pixel → skip the whole stamp. Trust the binarized
    // invariant: R+G+B < 60 ⇒ lineart.
    if (data[centerI]! + data[centerI + 1]! + data[centerI + 2]! < 60) return

    const homeMask = this.strokeHomeRegion
    const W = this.width
    // If a home mask is active and the stamp's center is OUTSIDE it,
    // the kid has dragged past a line into a different region — skip
    // the whole stamp so the stroke stays inside its home region.
    if (homeMask && !homeMask[cy * W + cx]) return

    const visited = new Uint8Array(w * h)
    visited[lcy * w + lcx] = 1
    const stack: number[] = [lcy * w + lcx]

    while (stack.length > 0) {
      const idx = stack.pop()!
      const px = idx % w
      const py = (idx - px) / w
      // Circle mask in canvas coords.
      const dx = px + x0 - cx
      const dy = py + y0 - cy
      if (dx * dx + dy * dy > r2) continue
      const i = idx * 4
      // Line barrier — leave the pixel and DON'T enqueue past it.
      if (data[i]! + data[i + 1]! + data[i + 2]! < 60) continue
      // Home-mask barrier — pixels outside the stroke's home region are
      // left untouched (and we skip enqueuing past them, since by
      // construction they're disconnected from the home region in mask
      // space, but checking here is the safe pattern).
      if (homeMask && !homeMask[(py + y0) * W + (px + x0)]) continue

      data[i] = cr
      data[i + 1] = cg
      data[i + 2] = cb
      data[i + 3] = 255

      // 4-neighbour expansion.
      if (px + 1 < w) {
        const n = idx + 1
        if (!visited[n]) {
          visited[n] = 1
          stack.push(n)
        }
      }
      if (px > 0) {
        const n = idx - 1
        if (!visited[n]) {
          visited[n] = 1
          stack.push(n)
        }
      }
      if (py + 1 < h) {
        const n = idx + w
        if (!visited[n]) {
          visited[n] = 1
          stack.push(n)
        }
      }
      if (py > 0) {
        const n = idx - w
        if (!visited[n]) {
          visited[n] = 1
          stack.push(n)
        }
      }
    }

    this.ctx.putImageData(region, x0, y0)
  }

  /** Push the current canvas state onto the history stack as one undo step
   *  AND clear the stroke home-region mask started by startBrushStroke.
   *  Call this after a brush stroke (pointerup) so the entire stroke
   *  collapses into a single undo. */
  commitBrushStroke(): void {
    this.history.pushFill(this.snapshot())
    this.strokeHomeRegion = null
  }

  /**
   * Magic autofill — detect all unfilled (near-white) connected regions on
   * the canvas, sample a representative color for each from the colored
   * reference image, and flood-fill them in one batched operation that
   * collapses into a SINGLE undo step.
   *
   * Caller passes an ImageData of the reference colored.jpg (same
   * dimensions as the canvas). Regions whose sampled color is invalid
   * (only near-white / near-black available across centroid + 8 fallbacks)
   * are skipped — they stay white.
   *
   * If nothing was filled (either no unfilled regions, or every region
   * skipped), no history snapshot is pushed and the canvas is not mutated.
   */
  magicAutofill(coloredData: ImageData): MagicFillResult {
    if (coloredData.width !== this.width || coloredData.height !== this.height) {
      throw new Error(
        `magicAutofill: colored reference ${coloredData.width}x${coloredData.height} does not match canvas ${this.width}x${this.height}`,
      )
    }
    const imageData = this.ctx.getImageData(0, 0, this.width, this.height)
    const result = runMagicAutofill({
      canvasData: imageData.data,
      coloredData: coloredData.data,
      width: this.width,
      height: this.height,
      threshold: this.options.threshold,
    })
    if (result.shouldCommit) {
      this.ctx.putImageData(imageData, 0, 0)
      this.history.pushFill(this.snapshot())
    }
    return { filled: result.filled, skipped: result.skipped }
  }

  undo(): boolean {
    const prev = this.history.undo()
    if (!prev) return false
    const id = this.ctx.createImageData(prev.width, prev.height)
    id.data.set(prev.data)
    this.ctx.putImageData(id, 0, 0)
    return true
  }

  canUndo(): boolean {
    return this.history.canUndo()
  }

  fillCount(): number {
    return this.history.fillCount()
  }

  resetToInitial(): boolean {
    const initial = this.history.resetToInitial()
    if (!initial) return false
    const id = this.ctx.createImageData(initial.width, initial.height)
    id.data.set(initial.data)
    this.ctx.putImageData(id, 0, 0)
    return true
  }

  toBlob(type = 'image/png', quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error('canvas.toBlob returned null'))
          else resolve(blob)
        },
        type,
        quality,
      )
    })
  }

  private snapshot() {
    const id = this.ctx.getImageData(0, 0, this.width, this.height)
    return { data: id.data, width: id.width, height: id.height }
  }

  /**
   * Trust-but-verify: if too many mid-gray pixels show up, this image probably
   * didn't go through process-assets.mjs and the engine's binarized assumption
   * is broken — flood fill will misbehave at edges. Warn early so we catch it
   * during dev rather than ship-day.
   */
  private checkBinarized(): void {
    const id = this.ctx.getImageData(0, 0, this.width, this.height)
    const data = id.data
    let midGray = 0
    // Walk RGBA, sample R (binarized lineart has R=G=B). Step by 4 (per pixel).
    for (let i = 0; i < data.length; i += 4) {
      const v = data[i]!
      if (v > 50 && v < 220) {
        midGray++
        if (midGray > MID_GRAY_WARN_LIMIT) break
      }
    }
    if (midGray > MID_GRAY_WARN_LIMIT) {
      console.warn(
        `[skena/coloring] lineart has ${midGray}+ mid-gray pixels. ` +
          'This image likely did not pass scripts/process-assets.mjs — ' +
          'flood fill may behave unexpectedly at edges.',
      )
    }
  }
}
